import { useSeo } from "../lib/seo";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";

const TYPES = ["all", "Recessive", "Incomplete Dominant", "Polygenic"];

export default function Genetics() {
  useSeo({
    title: "Boa Genetics | Quantum Boas",
    description: "A working encyclopedia of the boa constrictor morphs in the Quantum Boas program — what each gene does, how it inherits, and how it expresses.",
  });
  const [morphs, setMorphs] = useState([]);
  const [type, setType] = useState("all");

  useEffect(() => {
    api.get("/morphs").then((r) => setMorphs(r.data)).catch(() => {});
  }, []);

  const shown = morphs.filter((m) => type === "all" || m.trait_type === type);

  return (
    <div className="grain pt-32 pb-24 px-6 md:px-10 min-h-screen">
      <div className="max-w-[1400px] mx-auto">
        <span className="text-xs tracking-[0.25em] uppercase text-qbsecondary">Encyclopedia</span>
        <h1 className="font-display text-5xl sm:text-6xl md:text-7xl mt-4 mb-3">The genetics</h1>
        <p className="text-qbfg/55 max-w-2xl mb-10">
          A plain-language guide to every morph in our program — what it changes, how it inherits,
          and what to expect when you breed it.
        </p>

        <div className="flex flex-wrap gap-3 mb-12" data-testid="genetics-type-filters">
          {TYPES.map((t) => (
            <button key={t} onClick={() => setType(t)}
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                type === t ? "bg-qbprimary text-qbbg border-qbprimary" : "border-white/15 text-qbfg/70 hover:border-white/40"}`}>
              {t === "all" ? "All traits" : t}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="genetics-grid">
          {shown.map((m, i) => (
            <motion.div key={m.id}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
              data-testid={`morph-card-${m.id}`}
              className="group rounded-xl border border-white/10 bg-qbsurface overflow-hidden hover:border-white/25 transition-colors">
              <div className="p-6">
                <span className="inline-block text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full border mb-4"
                  style={{ color: m.accent, borderColor: `${m.accent}55` }}>{m.trait_type}</span>
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-2xl">{m.name}</h3>
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: m.accent, boxShadow: `0 0 12px ${m.accent}` }} />
                </div>
                <span className="text-xs text-qbfg/40 uppercase tracking-wider">{m.category}</span>
                <p className="text-sm text-qbfg/60 mt-3 leading-relaxed">{m.description}</p>
                {m.appearance && (
                  <p className="text-sm text-qbfg/45 mt-3 italic border-t border-white/5 pt-3">
                    <span className="text-qbfg/60 not-italic">Look: </span>{m.appearance}
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
